/**
 * The replay's timeline: the chapters as bands you click to skip to, what happened as ticks,
 * and the stops, silences and speeding under them — all on one axis with the playhead through
 * it, and dragging anywhere moves the playhead there. The speed chart, `SpeedChart`, sits
 * under it at the same width on the same axis, so the two playheads make one line.
 *
 * Positions are axis milliseconds, so a pre-booking's two quiet days take a sliver marked as
 * folded rather than the whole width.
 */

import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useTenantFormats } from '../shell/formats';
import { cx } from '../ui';
import { eventTone, toAxis, type Axis, type JourneyDetail } from './journey';

const CHAPTER_COLOUR: Record<string, string> = {
  call: 'bg-surface-3 text-ink-2',
  searching: 'bg-surface-3 text-ink-2',
  approach: 'bg-info-soft text-info',
  waiting: 'bg-warn-soft text-warn',
  aboard: 'bg-ok-soft text-ok',
};

const TICK_COLOUR: Record<string, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  danger: 'bg-danger',
  info: 'bg-info',
  neutral: 'bg-ink-3',
  brand: 'bg-brand',
};

export function JourneyTimeline({
  journey,
  axis,
  position,
  onSeek,
}: {
  journey: JourneyDetail;
  axis: Axis;
  /** The playhead, in axis milliseconds. */
  position: number;
  /** Somebody clicked or dragged: move the playhead to this axis position. */
  onSeek: (position: number) => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const track = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const percent = (real: string | number) =>
    (toAxis(axis, typeof real === 'number' ? real : Date.parse(real)) / Math.max(axis.length, 1)) *
    100;

  const seekTo = (event: ReactPointerEvent) => {
    const box = track.current?.getBoundingClientRect();

    if (!box || box.width === 0) return;

    const share = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1);

    onSeek(share * axis.length);
  };

  const head = (position / Math.max(axis.length, 1)) * 100;

  return (
    <div
      ref={track}
      className="relative select-none cursor-pointer touch-none"
      role="slider"
      tabIndex={-1}
      aria-label={t('reports.journeys.player.timeline')}
      aria-valuemin={0}
      aria-valuemax={Math.round(axis.length / 1000)}
      aria-valuenow={Math.round(position / 1000)}
      onPointerDown={(event) => {
        dragging.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        seekTo(event);
      }}
      onPointerMove={(event) => {
        if (dragging.current) seekTo(event);
      }}
      onPointerUp={(event) => {
        dragging.current = false;
        event.currentTarget.releasePointerCapture(event.pointerId);
      }}
    >
      {/* The chapters. */}
      <div className="relative h-7 rounded-control overflow-hidden bg-surface-2">
        {journey.chapters.map((chapter) => {
          const left = percent(chapter.from);
          const width = Math.max(percent(chapter.to) - left, 0.4);

          return (
            <div
              key={`${chapter.kind}-${chapter.from}`}
              className={cx(
                'absolute inset-y-0 flex items-center px-2 text-secondary font-semibold whitespace-nowrap overflow-hidden border-r border-surface',
                CHAPTER_COLOUR[chapter.kind] ?? 'bg-surface-3 text-ink-2',
              )}
              style={{ left: `${left}%`, width: `${width}%` }}
              title={`${t(`reports.journeys.chapter.${chapter.kind}`)} · ${formats.timeWithSeconds(chapter.from)}–${formats.timeWithSeconds(chapter.to)}`}
            >
              {t(`reports.journeys.chapter.${chapter.kind}`)}
            </div>
          );
        })}

        {axis.folds.map((fold) => (
          <div
            key={fold.from}
            className="absolute inset-y-0 w-1.5 -ml-0.75 bg-ground border-x border-rule"
            style={{ left: `${percent(fold.from) + (percent(fold.to) - percent(fold.from)) / 2}%` }}
            title={t('reports.journeys.player.folded', {
              time: formats.duration(Math.round((fold.to - fold.from) / 1000)),
            })}
          />
        ))}
      </div>

      {/* What happened, as ticks. */}
      <div className="relative h-4 mt-1">
        {journey.events.map((event, index) => (
          <span
            key={`${event.kind}-${event.at}-${index}`}
            className={cx(
              'absolute top-0.5 w-1 h-3 -ml-0.5 rounded-pill',
              TICK_COLOUR[eventTone(event.kind)],
            )}
            style={{ left: `${percent(event.at)}%` }}
            title={`${formats.timeWithSeconds(event.at)} · ${t(
              `reports.journeys.events.${event.kind}`,
              {
                driver: event.driverName ?? '',
                who: event.actorName ?? '',
              },
            )}`}
          />
        ))}

        {journey.calls.map((call) => (
          <span
            key={call.callId}
            className="absolute top-0.5 h-3 rounded-pill bg-brand opacity-70"
            style={{
              left: `${percent(call.startedAt)}%`,
              width: `${Math.max(percent(call.endedAt ?? call.startedAt) - percent(call.startedAt), 0.5)}%`,
            }}
            title={t('reports.journeys.player.callTick', {
              time: formats.timeWithSeconds(call.startedAt),
            })}
          />
        ))}
      </div>

      {/* Stops, silences and speeding. */}
      <div className="relative h-2.5">
        {journey.spans.map((span, index) => (
          <span
            key={`${span.kind}-${span.from}-${index}`}
            className={cx(
              'absolute top-0 h-2.5 rounded-pill',
              span.kind === 'speeding' && 'bg-danger',
              span.kind === 'silence' && 'bg-ink-3 opacity-60',
              span.kind === 'stop' && (span.flagged ? 'bg-warn' : 'bg-warn opacity-40'),
            )}
            style={{
              left: `${percent(span.from)}%`,
              width: `${Math.max(percent(span.to) - percent(span.from), 0.4)}%`,
            }}
            title={t(`reports.journeys.span.${span.kind}`, {
              time: formats.duration(
                Math.round((Date.parse(span.to) - Date.parse(span.from)) / 1000),
              ),
              speed: span.peakKmh ?? '',
              limit: span.limitKmh ?? '',
            })}
          />
        ))}
      </div>

      {/* The playhead, through all of it. */}
      <div
        className="absolute top-0 bottom-0 w-0.5 -ml-px bg-ink pointer-events-none"
        style={{ left: `${head}%` }}
        aria-hidden="true"
      >
        <span className="absolute -top-1 -left-1.25 w-3 h-3 rounded-full bg-ink border-2 border-surface" />
      </div>
    </div>
  );
}
